import { useLazyQuery } from '@apollo/client/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { UNIFIED_SEARCH } from '../graphql/queries/search';

import { EntityType } from './useRecentItems';

export interface SearchResult {
  id: string;
  metadata?: Record<string, string | undefined>;
  name: string;
  type: EntityType;
}

export interface SearchResultOpportunity {
  closingDate?: string;
  id: string;
  name: string;
  pipeline?: string;
  projectName?: string;
  stage?: string;
  stageDisplay?: string;
  status?: string;
  statusDisplay?: string;
}

export interface SearchResults {
  all: SearchResult[];
  opportunities: SearchResult[];
}

const DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 10;

/**
 * Hook for command palette search functionality
 * Handles debounced search and parallel GraphQL queries
 */
export function useCommandPalette() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResults>({
    all: [],
    opportunities: []
  });

  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const abortControllerRef = useRef<AbortController>();

  const [executeSearch, { data, error, loading }] = useLazyQuery(
    UNIFIED_SEARCH,
    {
      context: {
        fetchOptions: {
          signal: abortControllerRef.current?.signal
        }
      },
      fetchPolicy: 'network-only'
    }
  );

  // Debounce search term
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Store debounced term for use in processing
  const debouncedSearchTermRef = useRef(debouncedSearchTerm);
  useEffect(() => {
    debouncedSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  // Execute search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm.length >= 2) {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      executeSearch({
        context: {
          fetchOptions: {
            signal: abortControllerRef.current.signal
          }
        },
        variables: {
          limit: SEARCH_LIMIT,
          searchTerm: debouncedSearchTerm
        }
      });
    } else {
      // Clear results if search term is too short
      setResults({
        accounts: [],
        all: [],
        contacts: [],
        opportunities: [],
        views: []
      });
    }
  }, [debouncedSearchTerm, executeSearch]);

  // Process results from GraphQL
  useEffect(() => {
    if (data && debouncedSearchTerm.length >= 2) {
      const opportunities: SearchResult[] = (
        data.opportunities?.edges || []
      ).map((edge: { node: SearchResultOpportunity }) => ({
        id: edge.node.id,
        metadata: {
          closingDate: edge.node.closingDate,
          pipeline: edge.node.pipeline,
          projectName: edge.node.projectName,
          stage: edge.node.stageDisplay || edge.node.stage,
          status: edge.node.statusDisplay || edge.node.status
        },
        name: edge.node.name,
        type: 'opportunity' as EntityType
      }));

      setResults({
        all: opportunities,
        opportunities
      });
    }
  }, [data, debouncedSearchTerm]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setResults({
      all: [],
      opportunities: []
    });
  }, []);

  return {
    clearSearch,
    error,
    hasResults: results.all.length > 0,
    isEmpty: searchTerm.length === 0,
    loading,
    results,
    searchTerm,
    setSearchTerm
  };
}
